import {
  actionCardOwnChat,
  getInformationalCardKind,
  decodeActionCard,
  formatActionCardAge,
  HISTORY_ACTION_TYPES,
  getPendingActionCardTurnAnchorId,
  isActionCardDismissedByKind,
  isActionCardSnoozed,
  isFollowUpInProgress,
  isTodoActionCard,
  messagingChannelLabel,
  actionCardLastTouchedAt,
  nestUnderParents,
  partitionHomeActionCards,
  passesHomeCardFilters,
  selectChatPromptCards,
  useDeferredDismiss,
  waitsOnHome,
  type ActionCardSurface,
  type CardChildren,
  type ChatPromptCardEntry,
  type DecodedActionCard,
  type DeferredDismissState,
  type PendingActionCardFields,
} from '@littlebird/action-cards';
import { originOf } from '@littlebird/action-cards-view';
import {
  type ActionCard,
  type ActionCardDismissal,
  type Report,
  useStores,
} from '@littlebird/sdk';
import { getMeetingBucketDate } from '@littlebird/ui-utils';
import { partition as splitBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { surfaceOutcome, trackDismissal } from '@/features/action-cards';
import type { RunEnd } from '@/features/action-cards-view';
import { actionCardAnalyticsSnapshot } from '@/features/action-cards/analytics';
import { useDismissToast } from '@/features/action-cards/use-dismiss-toast';
import {
  feedHoldOfGroupLead,
  isMessageGroupSibling,
} from '@/features/hivemind/conversations/hivemind-conversations-status';
import { useRoutineRefresh } from '@/features/routines/use-routine-refresh';
import { useHiddenProactiveAgentChatIds } from '@/features/settings/proactive-agent.query';
import { useTabs } from '@/features/tabs';
import { trackEvent } from '@/utils/analytics';

import { attachActionCardToHomeComposer } from './follow-up-composer';
import { entryLastActiveAt } from './need-you-cards';
import { isFromHiddenAgentChat } from './proactive-agent-visibility';
import { useBackgroundRuns } from './use-background-runs';
import { useMinCardImportance } from './use-min-card-importance';
import { useRowTransitions } from './use-row-transitions';
import { useSnoozeClock } from './use-snooze-clock';
import { sourceChatIdOf, useSourceChats } from './use-source-chats';

// The fields `decodeActionCard` reads. Keying the cache on the card alone is
// the hazard the `'use no memo'` comment in `useActionCard.ts` warns about.
// Writes replace these fields instead of mutating them, so `===` sees changes.
const DECODER_FIELDS = [
  'actionType',
  'content',
  'externalRef',
  'result',
  'context',
  'status',
  'computed',
] as const;

const actionCardDecodeCache = new WeakMap<
  ActionCard,
  { readonly decoded: DecodedActionCard | null; readonly inputs: unknown[] }
>();

const decodeCached = (card: ActionCard): DecodedActionCard | null => {
  const inputs = DECODER_FIELDS.map((field) => card[field]);
  const cached = actionCardDecodeCache.get(card);
  if (cached && inputs.every((value, i) => value === cached.inputs[i])) {
    return cached.decoded;
  }

  const decoded = decodeActionCard(card);
  actionCardDecodeCache.set(card, { decoded, inputs });
  return decoded;
};

export type PendingActionCardEntry = {
  readonly card: ActionCard;
  readonly decoded: DecodedActionCard;
  // The cards under this one, when it is a parent the list shows
  readonly children?: CardChildren<PendingActionCardEntry, ActionCard>;
};

export type WaitingForYouListItem =
  | {
      readonly kind: 'provider';
      readonly id: string;
      readonly entry: PendingActionCardEntry;
    }
  | {
      readonly kind: 'chat_prompt';
      readonly id: string;
      readonly entry: ChatPromptCardEntry;
    };

export type PendingActionCardProvenance = {
  readonly sourceLabel: string;
  readonly ageLabel: string;
  readonly openSource?: () => void;
  // Set when a meeting made the card
  readonly meeting?: PendingActionCardMeeting;
};

export type PendingActionCardMeeting = {
  readonly name: string;
  // When the meeting started, or when the card was made while this device has not loaded the meeting
  readonly startedAt: Date;
};

const chatPromptItemId = (cardId: string) => `chat-prompt:${cardId}`;

const meetingOriginOf = (card: PendingActionCardFields) => {
  const origin = originOf(card.facts);
  return origin?.target?.type === 'meeting'
    ? { meetingId: origin.target.id, meetingName: origin.label ?? 'Meeting' }
    : undefined;
};

const listItemLastTouchedAt = (item: WaitingForYouListItem): number =>
  item.kind === 'provider'
    ? entryLastActiveAt(item.entry)
    : actionCardLastTouchedAt(item.entry.card);

export type WaitingForYouFollowUp = {
  readonly entries: readonly PendingActionCardEntry[];
  readonly attach: (card: ActionCard) => void;
  // Set only by a host that shows the card's chat beside the list
  readonly openInCardChat?: (card: ActionCard, seed?: string) => void;
};

export type WaitingForYouHistory = {
  readonly dismissed: readonly PendingActionCardEntry[];
  readonly done: readonly PendingActionCardEntry[];
  readonly withdrawn: readonly PendingActionCardEntry[];
};

export type WaitingForYouState = {
  readonly listItems: readonly WaitingForYouListItem[];
  readonly isItemExpanded: (itemId: string) => boolean;
  // The row is open, or a subtask under it is
  readonly isFamilyExpanded: (itemId: string) => boolean;
  // The one card open, a subtask included; undefined when none or several are
  readonly openCardId: string | undefined;
  readonly setItemExpanded: (itemId: string, expanded: boolean) => void;
  // Opens one row and closes every other; no id closes them all
  readonly openOnly: (itemId?: string) => void;
  readonly getProvenance: (
    card: PendingActionCardFields
  ) => PendingActionCardProvenance;
  readonly deferDismiss: (
    targets: readonly { readonly card: ActionCard }[],
    dismissal?: ActionCardDismissal,
    surface?: ActionCardSurface
  ) => void;
  readonly deferredDismiss: DeferredDismissState;
  // A retained row stays listed for the rest of the visit, also once its run is done
  readonly retainCards: (cardIds: readonly string[]) => void;
  // A launched card leaves the list while its run goes on behind a toast, and
  // comes back when the run does not go through
  readonly launchCard: (card: ActionCard) => void;
  readonly landCard: (card: ActionCard, end: RunEnd) => void;
  // False once the server refused a run, so the next run shows its errors in place
  readonly runsInBackground: (cardId: string) => boolean;
  // Set only when the host opts in with `animatesRowTransitions`. The host
  // plays the transition, then calls `endRowTransition`
  readonly rowTransition: (itemId: string) => RowTransition | undefined;
  readonly endRowTransition: (itemId: string) => void;
  // Counts cards that left the list for History during this visit
  readonly historyArrivals: number;
  // Empty unless the host asks for history
  readonly history: WaitingForYouHistory;
  readonly snoozed: readonly PendingActionCardEntry[];
  readonly followUp: WaitingForYouFollowUp;
};

export type RowTransition = 'completing' | 'dismissing' | 'returning';

type UseWaitingForYouOptions = {
  readonly acceptsCard?: (card: ActionCard) => boolean;
  readonly openComposer?: () => Promise<unknown>;
  readonly openCardChat?: (card: ActionCard) => void;
  readonly includeHistory?: boolean;
  // Keeps a completed or dismissed row listed until the host ends its exit.
  // An undone dismissal comes back as returning
  readonly animatesRowTransitions?: boolean;
};

export const useWaitingForYou = ({
  acceptsCard,
  openComposer,
  openCardChat,
  includeHistory = false,
  animatesRowTransitions = false,
}: UseWaitingForYouOptions = {}): WaitingForYouState => {
  const {
    actionCards,
    agents,
    meetings,
    messages,
    threads,
    reports,
    routines,
  } = useStores();
  const { minImportance } = useMinCardImportance();
  const hiddenAgentChatIds = useHiddenProactiveAgentChatIds();
  const { openTab } = useTabs();
  const [expandedItemIds, setExpandedItemIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const withoutExpanded = (itemId: string) =>
    setExpandedItemIds((current) =>
      current.has(itemId)
        ? new Set([...current].filter((id) => id !== itemId))
        : current
    );
  const {
    retainedCardIds,
    departedCardIds,
    historyArrivals,
    retainCards,
    endTransition,
  } = useRowTransitions(withoutExpanded);
  const { launchedCardIds, launchCard, landCard, runsInBackground } =
    useBackgroundRuns({
      retainCard: (cardId) => retainCards([cardId]),
      closeCard: withoutExpanded,
    });
  const deferredDismiss = useDeferredDismiss({
    onEvent: trackDismissal('home'),
    onOutcome: surfaceOutcome,
  });
  useDismissToast(deferredDismiss);

  // A dismissed row closes however the dismissal began, so Undo brings it back closed
  const deferredIdsKey = deferredDismiss.strips
    .flatMap((strip) => strip.targets.map((target) => target.cardId))
    .join('\n');
  useEffect(() => {
    if (!deferredIdsKey || animatesRowTransitions) return;
    const deferredIds = new Set(deferredIdsKey.split('\n'));
    setExpandedItemIds((current) =>
      [...current].some((id) => deferredIds.has(id))
        ? new Set([...current].filter((id) => !deferredIds.has(id)))
        : current
    );
  }, [deferredIdsKey, animatesRowTransitions]);

  const isChatDeleted = (chatId: string) =>
    !!threads.getById(chatId)?.deletedAt;

  // Read live models rather than snapshotting them: the store's `orderedData`
  // computed does not depend on a card's status, so a memoised copy would keep
  // showing cards that were confirmed or dismissed elsewhere.
  const snoozeNowMs = useSnoozeClock(actionCards.orderedData);
  const [snoozedCards, acceptedCards] = splitBy(
    actionCards.orderedData.filter(
      (card) =>
        !launchedCardIds.has(card.id) &&
        (!acceptsCard || acceptsCard(card)) &&
        !(
          card.chatId &&
          isMessageGroupSibling(card, actionCards.getByChatId(card.chatId))
        )
    ),
    (card) => isActionCardSnoozed(card, snoozeNowMs)
  );
  const [deferredCards, cards] = splitBy(acceptedCards, (card) =>
    deferredDismiss.isDeferred(card.id)
  );
  const dismissingCards = animatesRowTransitions
    ? deferredCards.filter((card) => !departedCardIds.has(card.id))
    : [];
  const dismissingCardIds = new Set(dismissingCards.map((card) => card.id));
  const partitionOptions = {
    isChatDeleted,
    isInProgress: (card: ActionCard) =>
      isFollowUpInProgress(card) ||
      (!!card.chatId &&
        feedHoldOfGroupLead(card, actionCards.getByChatId(card.chatId)) ===
          'following'),
    isDismissed: (card: ActionCard) =>
      card.status === 'dismissed' || isActionCardDismissedByKind(card),
    minImportance,
  };
  const partition = partitionHomeActionCards(cards, partitionOptions);

  const toEntry = (
    card: ActionCard,
    shows: (decoded: DecodedActionCard, card: ActionCard) => boolean = () =>
      true
  ): PendingActionCardEntry | null => {
    const decoded = decodeCached(card);
    return decoded && shows(decoded, card) ? { card, decoded } : null;
  };
  const toEntries = (
    cards: readonly ActionCard[],
    shows?: (decoded: DecodedActionCard, card: ActionCard) => boolean
  ): PendingActionCardEntry[] =>
    cards.flatMap((card) => toEntry(card, shows) ?? []);

  const listedInProgressEntries = toEntries(partition.inProgress);
  const doneRetainedCards = partition.done.filter((card) =>
    retainedCardIds.has(card.id)
  );
  // A message everyone answered stays in the Feed, with its answers, until the user marks them as seen
  const unseenAnswerCards = partition.done.filter(
    (card) =>
      !retainedCardIds.has(card.id) &&
      !!card.chatId &&
      feedHoldOfGroupLead(card, actionCards.getByChatId(card.chatId)) ===
        'unseen'
  );
  const completingCardIds = new Set(
    animatesRowTransitions ? doneRetainedCards.map((card) => card.id) : []
  );
  const showsOnFeed = (decoded: DecodedActionCard, card: ActionCard) =>
    waitsOnHome(card.actionType, decoded.capability);
  const listedEntries = toEntries(
    [
      ...partition.pending,
      ...doneRetainedCards,
      ...unseenAnswerCards,
      ...dismissingCards,
    ],
    showsOnFeed
  );
  const showsInHistory = (decoded: DecodedActionCard, card: ActionCard) =>
    HISTORY_ACTION_TYPES.keptActionTypes.includes(card.actionType) ||
    isTodoActionCard(decoded.content);
  const historyEntries = (cards: readonly ActionCard[]) =>
    includeHistory ? toEntries(cards, showsInHistory) : [];
  const dismissedEntries = historyEntries(partition.dismissed);
  const doneEntries = historyEntries(partition.done);
  const withdrawnEntries = historyEntries(partition.withdrawn);
  const chatPromptEntries = selectChatPromptCards(cards);
  // Snoozed cards skip the partition, so they pass its filters here
  const snoozedEntries = toEntries(
    snoozedCards.filter((card) =>
      passesHomeCardFilters(card, partitionOptions)
    ),
    showsOnFeed
  );

  // A snoozed parent keeps no subtasks: a subtask that is not snoozed itself stays in the main lists
  const nested = nestUnderParents(
    { pending: listedEntries, inProgress: listedInProgressEntries },
    cards,
    {
      ...partitionOptions,
      toEntry: (card: ActionCard) => toEntry(card, showsOnFeed),
    }
  );
  const entries = nested.pending;
  const inProgressEntries = nested.inProgress;
  const nestedEntries = [...entries, ...inProgressEntries].flatMap(
    (entry) => entry.children?.open ?? []
  );
  const subtaskIdsByParent = new Map(
    [...entries, ...inProgressEntries].flatMap(
      ({ card, children }): [string, string[]][] =>
        children ? [[card.id, children.open.map((child) => child.card.id)]] : []
    )
  );
  const listedCardIds = new Set(
    [...entries, ...inProgressEntries, ...snoozedEntries, ...nestedEntries].map(
      ({ card }) => card.id
    )
  );
  const openCardIds = [...expandedItemIds].filter((id) =>
    listedCardIds.has(id)
  );

  const hasRoutineCards = entries.some(({ card }) => card.origin === 'routine');
  useRoutineRefresh(hasRoutineCards);
  const orderedReports = reports.orderedReports;
  const orderedRoutines = routines.orderedData;

  const reportsByChatId = useMemo(() => {
    const byChatId = new Map<string, Report>();
    for (const report of orderedReports) {
      if (report.chatId) byChatId.set(report.chatId, report);
    }
    return byChatId;
  }, [orderedReports]);

  const routinesById = useMemo(
    () =>
      new Map(orderedRoutines.map((routine) => [routine.referenceId, routine])),
    [orderedRoutines]
  );

  const listItems = [
    ...entries.map((entry): WaitingForYouListItem => ({
      kind: 'provider',
      id: entry.card.id,
      entry,
    })),
    ...chatPromptEntries.map((entry): WaitingForYouListItem => ({
      kind: 'chat_prompt',
      id: chatPromptItemId(entry.card.id),
      entry,
    })),
  ].sort(
    (left, right) =>
      listItemLastTouchedAt(right) - listItemLastTouchedAt(left) ||
      left.id.localeCompare(right.id)
  );
  const chatIdsKey = [
    ...new Set(
      [...entries, ...nestedEntries].flatMap(({ card }) =>
        card.chatId &&
        !card.sourceMessagingChannelType &&
        // A meeting card's chat is the meeting's hidden meeting assist chat,
        // which the app never shows
        !card.sourceMeetingId &&
        expandedItemIds.has(card.id)
          ? [card.chatId]
          : []
      )
    ),
  ].join('\n');
  useEffect(() => {
    if (!chatIdsKey) return;
    void agents.ensureListLoadedFromStorage();
    for (const chatId of chatIdsKey.split('\n')) {
      void threads.loadThread(chatId);
      void messages.loadThreadMessages(chatId);
    }
  }, [chatIdsKey, agents, threads, messages]);
  // An open meeting card names the day of its meeting, which loads on demand like a chat
  const meetingIdsKey = [
    ...new Set(
      [
        ...entries,
        ...nestedEntries,
        ...inProgressEntries,
        ...snoozedEntries,
      ].flatMap(({ card }) => {
        const meeting = meetingOriginOf(card);
        return meeting && expandedItemIds.has(card.id)
          ? [meeting.meetingId]
          : [];
      })
    ),
  ].join('\n');
  useEffect(() => {
    if (meetingIdsKey) void meetings.loadByIds(meetingIdsKey.split('\n'));
  }, [meetingIdsKey, meetings]);

  // Chats load on demand, and a card links to its chat only once it is loaded
  useSourceChats(
    [
      ...entries,
      ...nestedEntries,
      ...inProgressEntries,
      ...dismissedEntries,
      ...doneEntries,
      ...chatPromptEntries,
    ].map(({ card }) => card)
  );

  const rowTransition = (itemId: string): RowTransition | undefined => {
    if (!animatesRowTransitions) return undefined;
    if (dismissingCardIds.has(itemId)) return 'dismissing';
    if (completingCardIds.has(itemId)) return 'completing';
    return departedCardIds.has(itemId) ? 'returning' : undefined;
  };

  const endRowTransition = (itemId: string) => {
    const transition = rowTransition(itemId);
    if (transition) endTransition(itemId, transition);
  };

  // A source the user may not open never syncs here, so a card links only to
  // a routine run whose routine is here, or to a chat that is here
  const heldRoutineRun = (card: PendingActionCardFields) => {
    const report = card.chatId ? reportsByChatId.get(card.chatId) : undefined;
    const routineId = card.reportTemplateId ?? report?.reportTemplateId;
    const reportId = card.reportId ?? report?.referenceId;
    if (routineId === undefined || reportId === undefined) return undefined;
    const title = routinesById.get(routineId)?.title;
    return title === undefined ? undefined : { routineId, reportId, title };
  };

  const isFromHiddenAgent = (card: PendingActionCardFields) =>
    isFromHiddenAgentChat(card, hiddenAgentChatIds, {
      reportByChatId: (chatId) => reportsByChatId.get(chatId),
      routineByReferenceId: (referenceId) => routinesById.get(referenceId),
    });

  // The user cannot see a routine or chat that is not on this device, so the card names Littlebird
  const routineTitle = (card: PendingActionCardFields) =>
    heldRoutineRun(card)?.title || 'Littlebird';
  const chatTitle = (card: PendingActionCardFields) => {
    const thread = card.chatId ? threads.getById(card.chatId) : undefined;
    return thread ? thread.title || 'Chat' : 'Littlebird';
  };

  const agentNameFor = (card: PendingActionCardFields) => {
    const thread = card.chatId ? threads.getById(card.chatId) : undefined;
    if (!thread?.agentId) return undefined;
    return agents.getById(thread.agentId)?.name || thread.title || 'Agent';
  };

  const openChat = (chatId: string) =>
    openTab({ path: `/chats/${chatId}` }, { reuseExisting: true });
  const openOwnChat = (card: ActionCard, chatId: string) =>
    openCardChat ? openCardChat(card) : openChat(chatId);

  const openSourceFor = (card: PendingActionCardFields) => {
    const trackOpen = () =>
      trackEvent(
        'ACTION_CARD_SOURCE_OPENED',
        actionCardAnalyticsSnapshot(card, 'home', {
          card_expanded: expandedItemIds.has(card.id),
        })
      );
    const meeting = meetingOriginOf(card);
    if (meeting) {
      return () => {
        trackOpen();
        openTab(
          {
            path: `/meetings/${meeting.meetingId}`,
            search: { tab: 'assist' },
          },
          { reuseExisting: true }
        );
      };
    }
    const run = heldRoutineRun(card);
    if (run) {
      return () => {
        trackOpen();
        openTab(
          {
            path: `/routines/${run.routineId}`,
            search: { view: 'canvas', run: run.reportId },
          },
          { reuseExisting: true }
        );
      };
    }
    const chatId = sourceChatIdOf(card);
    if (!chatId || !threads.getById(chatId)) return undefined;
    return () => {
      trackOpen();
      openTab(
        { path: `/chats/${chatId}` },
        {
          reuseExisting: true,
          anchorMessageId: getPendingActionCardTurnAnchorId(
            card,
            messages.getMessagesByThreadId(chatId)
          ),
        }
      );
    };
  };

  const getProvenance = (
    card: PendingActionCardFields
  ): PendingActionCardProvenance => {
    const meeting = meetingOriginOf(card);
    if (meeting) {
      // The day the meetings list files the meeting under
      const record = meetings.get(meeting.meetingId);
      const startedAt = record
        ? getMeetingBucketDate(record)
        : new Date(card.createdAt);
      return {
        sourceLabel: meeting.meetingName,
        ageLabel: formatActionCardAge(card.createdAt),
        openSource: openSourceFor(card),
        meeting: { name: meeting.meetingName, startedAt },
      };
    }
    if (card.origin === 'user') {
      return {
        sourceLabel: 'You',
        ageLabel: formatActionCardAge(card.createdAt),
      };
    }
    if (card.origin === 'littlebird_admin' || isFromHiddenAgent(card)) {
      return {
        sourceLabel: 'Littlebird',
        ageLabel: formatActionCardAge(card.createdAt),
      };
    }
    const sourceChannelLabel = messagingChannelLabel(card);
    return {
      sourceLabel:
        sourceChannelLabel ||
        (card.origin === 'routine'
          ? routineTitle(card)
          : agentNameFor(card) || chatTitle(card)),
      ageLabel: formatActionCardAge(card.createdAt),
      openSource: openSourceFor(card),
    };
  };

  const deferDismiss = (
    targets: readonly { readonly card: ActionCard }[],
    dismissal?: ActionCardDismissal,
    surface?: ActionCardSurface
  ) => {
    deferredDismiss.beginDismiss(
      targets.map(({ card }) => ({
        cardId: card.id,
        actionType: card.actionType,
        cardKind: getInformationalCardKind(card),
        origin: card.origin,
        surface,
        analytics: actionCardAnalyticsSnapshot(card, surface ?? 'home'),
      })),
      dismissal
    );
  };

  const setItemExpanded = (itemId: string, expanded: boolean) => {
    setExpandedItemIds((current) => {
      if (current.has(itemId) === expanded) return current;
      const next = new Set(current);
      if (expanded) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const openOnly = (itemId?: string) =>
    setExpandedItemIds((current) =>
      current.size === (itemId ? 1 : 0) && (!itemId || current.has(itemId))
        ? current
        : new Set(itemId ? [itemId] : [])
    );

  return {
    listItems,
    isItemExpanded: (itemId) => expandedItemIds.has(itemId),
    isFamilyExpanded: (itemId) =>
      expandedItemIds.has(itemId) ||
      !!subtaskIdsByParent
        .get(itemId)
        ?.some((subtaskId) => expandedItemIds.has(subtaskId)),
    openCardId: openCardIds.length === 1 ? openCardIds[0] : undefined,
    setItemExpanded,
    openOnly,
    getProvenance,
    deferDismiss,
    deferredDismiss,
    retainCards,
    launchCard,
    // The run may have moved the card on since the press, so the store's copy says how it ended
    landCard: (card, end) => landCard(actionCards.get(card.id) ?? card, end),
    runsInBackground,
    rowTransition,
    endRowTransition,
    historyArrivals,
    history: {
      dismissed: dismissedEntries,
      done: doneEntries,
      withdrawn: withdrawnEntries,
    },
    snoozed: snoozedEntries,
    followUp: {
      entries: inProgressEntries,
      attach: (card) => {
        const ownChat = actionCardOwnChat(card);
        if (ownChat) {
          openOwnChat(card, ownChat.chatId);
          return;
        }
        const attach = () => {
          trackEvent(
            'ACTION_CARD_FOLLOW_UP_ATTACHED',
            actionCardAnalyticsSnapshot(card, 'home')
          );
          attachActionCardToHomeComposer(card);
        };
        if (openComposer) void openComposer().then(attach);
        else attach();
      },
    },
  };
};
